import {
  Button,
  FlexBox,
  Icon,
  Switch,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useState } from 'react';

import type { BulletinMeta } from 'apis/bulletinboards';

import styles from './bulletinListItem.module.scss';

type BulletinListItemProps = {
  /** Bulletin to display/edit */
  bulletin: BulletinMeta;
  /** Disables the inputs while the bulletin is updating */
  isUpdating: boolean;
  /**
   * Flag to say if the BulletinListItem is currently being dragged, so we can
   * render it half opacity
   */
  isDragging: boolean;
  /**
   * Triggered when the enabled switch is changed.
   *
   * @param bulletin The bulletin item
   * @param enabled The new enabled setting
   */
  setEnabled: (bulletin: BulletinMeta, enabled: boolean) => void;
  /**
   * Triggered when the edit button is pressed.
   *
   * @param bulletin The bulletin item
   */
  onEdit: (bulletin: BulletinMeta) => void;
  /**
   * Triggered when the delete buttin is pressed.
   *
   * @param bullein The bulletin item
   */
  onDelete: (bulletin: BulletinMeta) => void;
};

export function BulletinListItem({
  isUpdating,
  isDragging,
  bulletin,
  setEnabled,
  onDelete,
  onEdit,
}: BulletinListItemProps): JSX.Element {
  const [isHovering, setIsHovering] = useState(false);
  const cantEnable = bulletin.title.length === 0;

  return (
    <div
      className={classnames(styles.row, isDragging && styles.isDragging)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div
        className={classnames(
          'handle',
          styles.handle,
          isHovering && styles.visible,
        )}
      >
        <Icon name="bars" />
      </div>

      <FlexBox
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        background={isHovering ? 'neutral.200' : 'neutral.100'}
        borderRadius="4px"
        borderColor={isHovering ? 'neutral.300' : 'neutral.100'}
      >
        <Tooltip
          disabled={!cantEnable}
          content="Bulletin must have a title before publishing."
          dark
        >
          <FlexBox alignItems="center" margin="250">
            <Switch
              disabled={isUpdating || cantEnable}
              name=""
              value=""
              checked={bulletin.active}
              onChange={(e) => setEnabled(bulletin, e.target.checked)}
            />
            <Text.Paragraph variant="2">{bulletin.title}</Text.Paragraph>
          </FlexBox>
        </Tooltip>

        {isHovering && (
          <FlexBox margin="0 250">
            <Tooltip content="Edit" dark>
              <Button
                variant="tertiary"
                disabled={isUpdating}
                onClick={() => onEdit(bulletin)}
              >
                <Icon name="pen" />
              </Button>
            </Tooltip>

            <Tooltip content="Delete" dark>
              <Button
                variant="tertiary"
                disabled={isUpdating}
                onClick={() => onDelete(bulletin)}
              >
                <Icon name="trash" />
              </Button>
            </Tooltip>
          </FlexBox>
        )}
      </FlexBox>
    </div>
  );
}
